import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { Issue, RegisteredAuditResourceType } from '@resource-kit/model';
import type { ResourceTabGateContext, Translate } from '@resource-kit/core';
import type { EntityField } from '@resource-kit/controllers/react';

/**
 * The form view's host slots (#2168, ADR-0035 D3).
 *
 * The rule this file exists to serve: **the host provides DATA and POLICY
 * through the services seam, and RENDERING through a slot.** The change
 * history is the second kind. The host's change-history component is an hgs
 * business composite — it matches Shopify actors, reads the host's route
 * table, and imports `@/services`, `react-i18next` and `@/i18n`, the last two
 * of which the seam guardrail forbids inside these directories outright — so
 * no amount of seam design gets it into the kit. What the kit can own is the
 * SHAPE of the hole it leaves, which is all this is.
 *
 * The slot carries the permission decision as well as the renderer. That is
 * not a convenience: the ledger-read grant is named out of the host's
 * permission catalogue, and `ResourceDetailPanel` naming it was the last
 * thing keeping that catalogue inside `components/resource/`. The panel now
 * hands the seam's `access` whole and lets the host's policy answer.
 *
 * Every member is REQUIRED, and a missing provider throws rather than
 * degrading — a slot with no wiring is a composition-root mistake, so it must
 * fail at assembly with a legible message rather than render a silently empty
 * tab. `useFormViewSlots` below is where that throw lives.
 *
 * The interface is deliberately NOT `Readonly` and not sealed. #2169 PR-3 took
 * that option up: `describeError` is the second member, and a frozen shape
 * would have had to be reopened for it.
 */

/**
 * Exactly the props `FormView` used to pass the host's change-history
 * component, field for field. `EntityField` lives in the host's `types/schema`
 * and
 * `RegisteredAuditResourceType` in `@resource-kit/model` — which depends on
 * core — so this contract can never move down into core. It travels with the
 * ui package instead.
 */
export interface ChangeHistorySlotProps<T> {
  resourceType: RegisteredAuditResourceType;
  resourceId: string;
  fields: readonly EntityField<T>[];
}

export interface ChangeHistorySlot {
  /**
   * Host policy: may this identity read the change ledger? Shape =
   * `ResourceTabGateContext`
   * (`packages/resource-kit-core/services.ts::ResourceTabGateContext`).
   */
  canView: (access: ResourceTabGateContext) => boolean;
  /**
   * Invoked as a plain FUNCTION during `FormView`'s render — never mounted as
   * a component, and only while `FormView`'s ledger gate
   * (`!isCreate && canViewAudit && model.auditResourceType != null`) and the
   * gate keeps an `audit` node in the tree — so the implementation must call
   * no hooks of its own. Return an element and let THAT component own its
   * hooks. FormView builds this element for every tab on every render; only
   * the selected tab mounts it.
   */
  render: <T>(props: ChangeHistorySlotProps<T>) => ReactNode;
}

/**
 * Turn a failed write into the two things the form renders: a form-level
 * message, and per-field issues on the kit's own `Issue` vocabulary.
 *
 * The SECOND slot, and it is here for the same reason as the first — the host
 * provides data and policy through the services seam, rendering and
 * host-shaped ADAPTERS through a slot. Both halves of this one are adapters:
 * what a failed request looks like on the wire is a property of the host's HTTP
 * client, and which key names the copy for it is a property of the host's
 * catalogs. Neither is a fact `@resource-kit/ui` can know (ADR-0035 D1).
 *
 * It does NOT belong in the services seam next door: the result carries
 * `Issue<T>`, which lives in `@resource-kit/model`, and `@resource-kit/core`
 * does not depend on model. A seam member returning it would invert that.
 *
 * The context is a PARAMETER rather than something the implementation closes
 * over, because `hgsFormViewSlots` is a module-level constant and cannot call
 * `useResourceKit()`. `FormView` reads the seam and hands the three values
 * down: `t` translates, `exists` answers whether a resource-scoped key is in
 * the catalog at all, and `namespace` is the resource slug those keys are
 * grouped under. `t` and `exists` are separate on purpose — a probe that also
 * translated would be a second translator.
 *
 * ONE CALL per failure: `message` and `issues` describe the SAME error, and
 * calling twice would let a stateful describer answer inconsistently.
 */
export interface DescribeErrorContext {
  t: Translate;
  exists: (key: string) => boolean;
  namespace: string;
}

export interface FormViewSlots {
  changeHistory: ChangeHistorySlot;
  describeError: <T>(
    error: unknown,
    ctx: DescribeErrorContext,
  ) => { message: string; issues: Issue<T>[] };
}

const FormViewSlotsContext = createContext<FormViewSlots | null>(null);

export function FormViewSlotsProvider({
  children,
  ...slots
}: FormViewSlots & { children: ReactNode }) {
  const value = useMemo<FormViewSlots>(
    () => ({ changeHistory: slots.changeHistory, describeError: slots.describeError }),
    // A member added to `FormViewSlots` later must be added BOTH to the
    // object literal above (TypeScript enforces that only if the member is
    // required) and to this deps array (TypeScript does NOT) — otherwise it is
    // memoised stale.
    [slots.changeHistory, slots.describeError],
  );
  return <FormViewSlotsContext.Provider value={value}>{children}</FormViewSlotsContext.Provider>;
}

export function useFormViewSlots(): FormViewSlots {
  const value = useContext(FormViewSlotsContext);
  if (value === null) {
    throw new Error('resource-kit: this hook must be used inside <FormViewSlotsProvider>');
  }
  return value;
}
